import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import Backend from "i18next-http-backend"
import { isBrowser } from "~/utils/is-browser"

export const SUPPORTED_LANGUAGES = ["en", "fr"] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

const LANGUAGE_STORAGE_KEY = "preferredLanguage"

const isSupportedLanguage = (value: string | null): value is SupportedLanguage =>
  SUPPORTED_LANGUAGES.includes(value as SupportedLanguage)

export const getStoredLanguage = (): SupportedLanguage | undefined => {
  if (!isBrowser) return undefined
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (isSupportedLanguage(stored)) {
    return stored
  }
  return undefined
}

export const persistLanguage = (language: SupportedLanguage) => {
  if (!isBrowser) return
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
}

const resolveInitialLanguage = (): SupportedLanguage => {
  const stored = getStoredLanguage()
  if (stored) return stored

  if (isBrowser) {
    const navigatorLanguage = window.navigator.language.split("-")[0]
    if (isSupportedLanguage(navigatorLanguage)) {
      return navigatorLanguage
    }
  }

  return "en"
}

const getLocalesPath = (): string => {
  // In production, assets are served from /dist/ due to S3/CloudFront setup
  // Use webpack DefinePlugin to inject the base path at build time
  const basePath = process.env.I18N_BASE_PATH || ""

  // If basePath is empty, use root path; otherwise prepend it
  return basePath ? `${basePath}/locales/{{lng}}/{{ns}}.json` : `/locales/{{lng}}/{{ns}}.json`
}

export default () => {
  if (!i18n.isInitialized) {
    i18n
      .use(Backend)
      .use(initReactI18next)
      .init({
        backend: {
          loadPath: getLocalesPath(),
        },
        supportedLngs: SUPPORTED_LANGUAGES,
        fallbackLng: "en",
        lng: resolveInitialLanguage(),
        interpolation: {
          escapeValue: false, // react already safes from xss
        },
      })
  }

  return i18n
}
